import { uiStore } from './ui.js';

/**
 * Efekty dźwiękowe gry.
 *
 * Dźwięki są **syntezowane**, nie wczytywane z plików: zero zasobów do
 * pobrania i licencjonowania, zero opóźnienia przy pierwszym odtworzeniu,
 * kilkadziesiąt linii zamiast katalogu z nagraniami. Do krótkich sygnałów
 * w quizie proste tony brzmią właściwie — to zresztą estetyka teleturnieju.
 *
 * Dźwięk nigdy nie jest jedynym nośnikiem informacji: trafienie i pomyłka
 * są widoczne także przy wyciszeniu, a sygnał je tylko wzmacnia.
 */

/** @type {AudioContext | null} */
let context = null;

/**
 * Kontekst audio wolno utworzyć dopiero w reakcji na gest użytkownika —
 * przeglądarki blokują dźwięk do pierwszej interakcji. W praktyce powstaje
 * przy kliknięciu „Start", więc nikogo nie zaskoczy hałas po wejściu na stronę.
 */
function ensureContext() {
  if (uiStore.get().muted) return null;

  if (!context) {
    const Ctor = window.AudioContext ?? window.webkitAudioContext;
    if (!Ctor) return null; // przeglądarka bez Web Audio — gra działa bez dźwięku
    context = new Ctor();
  }
  if (context.state === 'suspended') context.resume();

  return context;
}

/**
 * Pojedynczy ton z obwiednią.
 *
 * Obwiednia jest istotna: ton włączony i wyłączony na sztywno daje słyszalne
 * trzaśnięcie na krawędziach.
 */
function tone(ctx, { frequency, start = 0, duration = 0.12, type = 'sine', gain = 0.14 }) {
  const oscillator = ctx.createOscillator();
  const envelope = ctx.createGain();

  oscillator.type = type;
  oscillator.frequency.setValueAtTime(frequency, ctx.currentTime + start);

  envelope.gain.setValueAtTime(0, ctx.currentTime + start);
  envelope.gain.linearRampToValueAtTime(gain, ctx.currentTime + start + 0.012);
  envelope.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + duration);

  oscillator.connect(envelope).connect(ctx.destination);
  oscillator.start(ctx.currentTime + start);
  oscillator.stop(ctx.currentTime + start + duration + 0.02);
}

const EFFECTS = {
  /** Krótkie stuknięcie odliczania — ostatnie sekundy. */
  tick: (ctx) => tone(ctx, { frequency: 880, duration: 0.05, type: 'square', gain: 0.06 }),

  /** Trafienie: dwa tony w górę, tercja. */
  correct: (ctx) => {
    tone(ctx, { frequency: 660, duration: 0.1 });
    tone(ctx, { frequency: 990, start: 0.09, duration: 0.16 });
  },

  /** Pomyłka: krótki opad, wyraźnie niższy. */
  wrong: (ctx) => {
    tone(ctx, { frequency: 200, duration: 0.18, type: 'sawtooth', gain: 0.1 });
    tone(ctx, { frequency: 150, start: 0.1, duration: 0.2, type: 'sawtooth', gain: 0.08 });
  },

  /** Koniec quizu: trójdźwięk w górę. */
  finish: (ctx) => {
    tone(ctx, { frequency: 523, duration: 0.14 });
    tone(ctx, { frequency: 659, start: 0.12, duration: 0.14 });
    tone(ctx, { frequency: 784, start: 0.24, duration: 0.3 });
  },
};

/** @param {'tick'|'correct'|'wrong'|'finish'} name */
export function play(name) {
  const ctx = ensureContext();
  if (!ctx) return;

  try {
    EFFECTS[name]?.(ctx);
  } catch {
    // Dźwięk jest dodatkiem — jego awaria nie może przerwać rozgrywki.
  }
}

/**
 * Budzi kontekst audio przy pierwszym geście użytkownika.
 * Wywoływane z przycisku startu, zanim padnie pierwszy dźwięk.
 */
export function unlock() {
  ensureContext();
}
